import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { upload_cms_photo } from "../../reduxData/cms/cmsAction";
import { resolveImageUrl } from "../CMSPages/HomepageHeroSlides/homepageHero.helpers";
import { toEmbedUrl } from "./knowledgeBase.helpers";

// Uploaded videos are stored as <video src controls>; Quill's built-in
// "video" format (iframe) is kept for YouTube / Vimeo embeds.
const BlockEmbed = Quill.import("blots/block/embed");
class UploadedVideo extends BlockEmbed {
  static create(url) {
    const node = super.create();
    node.setAttribute("src", url);
    node.setAttribute("controls", "");
    return node;
  }
  static value(node) {
    return node.getAttribute("src");
  }
}
UploadedVideo.blotName = "kbVideo";
UploadedVideo.tagName = "video";
if (!Quill.imports["formats/kbVideo"]) Quill.register(UploadedVideo);

const MAX_IMAGE_MB = 10;
const IMAGE_SIZES = [25, 50, 75, 100];

export default function KnowledgeBaseEditor({ value, onChange, disabled = false, hasError = false, toolbarId = "kb-editor-toolbar", placeholder = "Write the article…" }) {
  const quillRef = useRef(null);
  const wrapRef = useRef(null);
  const dispatch = useDispatch();
  const [selectedImg, setSelectedImg] = useState(null);
  const [videoModal, setVideoModal] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [videoError, setVideoError] = useState("");
  const savedIndex = useRef(null);

  const closeVideoModal = () => {
    setVideoModal(false);
    setVideoUrl("");
    setVideoError("");
  };

  const insertVideo = (event) => {
    event.preventDefault();
    // The modal is a React portal inside the article <form>; stop its submit from reaching that form.
    event.stopPropagation();
    const embedUrl = toEmbedUrl(videoUrl);
    if (!embedUrl) {
      setVideoError("Enter a valid YouTube or Vimeo link.");
      return;
    }
    const editor = quillRef.current.getEditor();
    const index = savedIndex.current ?? editor.getLength();
    editor.insertEmbed(index, "video", embedUrl, "user");
    editor.setSelection(index + 1);
    closeVideoModal();
  };
  const [imgBar, setImgBar] = useState({ top: 0, left: 0 });

  const selectImage = (img) => {
    if (!img) return setSelectedImg(null);
    const wrap = wrapRef.current.getBoundingClientRect();
    const rect = img.getBoundingClientRect();
    setImgBar({ top: rect.top - wrap.top + 8, left: rect.left - wrap.left + 8 });
    setSelectedImg(img);
  };

  useEffect(() => {
    const root = quillRef.current?.getEditor().root;
    if (!root || disabled) return undefined;
    const onClick = (e) => {
      if (e.target.tagName === "IMG") return selectImage(e.target);
      // iframes ignore pointer events inside the editor, so hit-test them by position.
      const frame = [...root.querySelectorAll("iframe")].find((node) => {
        const r = node.getBoundingClientRect();
        return e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      });
      selectImage(frame || null);
    };
    const onScroll = () => setSelectedImg(null);
    root.addEventListener("click", onClick);
    root.addEventListener("keydown", onScroll);
    return () => {
      root.removeEventListener("click", onClick);
      root.removeEventListener("keydown", onScroll);
    };
  }, [disabled]);

  const resizeImage = (percent) => {
    const editor = quillRef.current.getEditor();
    const blot = Quill.find(selectedImg);
    if (!blot) return;
    const index = editor.getIndex(blot);
    editor.formatText(index, 1, { width: `${percent}%`, height: false }, "user");
    requestAnimationFrame(() => selectImage(selectedImg));
  };

  const currentWidth = parseInt(selectedImg?.getAttribute("width"), 10) || 100;

  const pickFile = (accept, maxMb, embed) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = accept;
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      if (file.size > maxMb * 1024 * 1024) {
        toast.error(`File is too large (max ${maxMb} MB)`);
        return;
      }
      try {
        const res = await upload_cms_photo([file], dispatch);
        const path = res?.data?.data?.[0] ?? res?.data?.[0] ?? res?.data?.path;
        if (typeof path !== "string" || !path.trim()) throw new Error("No path in upload response");
        const url = resolveImageUrl(path);
        const editor = quillRef.current.getEditor();
        const index = (editor.getSelection(true) || { index: editor.getLength() }).index;
        editor.insertEmbed(index, embed, url, "user");
        editor.setSelection(index + 1);
      } catch (e) {
        toast.error("Upload failed. Please try again.");
      }
    };
    input.click();
  };

  const modules = useMemo(
    () => ({
      toolbar: {
        container: `#${toolbarId}`,
        handlers: {
          image: () => pickFile("image/*", MAX_IMAGE_MB, "image"),
          videoUrl: () => {
            const editor = quillRef.current.getEditor();
            savedIndex.current = (editor.getSelection(true) || { index: editor.getLength() }).index;
            setVideoModal(true);
          },
        },
      },
    }),
    [toolbarId],
  );

  return (
    <div ref={wrapRef} className={`kb-editor${hasError ? " kb-editor--error" : ""}`}>
      <div id={toolbarId}>
        <span className="ql-formats">
          <select className="ql-header" defaultValue="">
            <option value="1" />
            <option value="2" />
            <option value="3" />
            <option value="" />
          </select>
        </span>
        <span className="ql-formats">
          <button className="ql-bold" />
          <button className="ql-italic" />
          <button className="ql-underline" />
        </span>
        <span className="ql-formats">
          <button className="ql-list" value="ordered" />
          <button className="ql-list" value="bullet" />
          <button className="ql-blockquote" />
        </span>
        <span className="ql-formats">
          <button className="ql-link" />
          <button className="ql-image" title="Upload image" />
          <button className="ql-videoUrl kb-tool" title="Embed YouTube / Vimeo video">Video</button>
        </span>
        <span className="ql-formats">
          <button className="ql-clean" />
        </span>
      </div>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value || ""}
        modules={modules}
        readOnly={disabled}
        placeholder={placeholder}
        onChange={(html, ...rest) => {
          setSelectedImg(null);
          onChange(html, ...rest);
        }}
      />
      {selectedImg && (
        <div className="kb-img-bar" style={{ top: imgBar.top, left: imgBar.left }} onMouseDown={(e) => e.preventDefault()}>
          {IMAGE_SIZES.map((size) => (
            <button
              key={size}
              type="button"
              className={currentWidth === size ? "active" : ""}
              onClick={() => resizeImage(size)}
            >
              {size}%
            </button>
          ))}
        </div>
      )}
      <Modal show={videoModal} centered onHide={closeVideoModal}>
        <form onSubmit={insertVideo} noValidate>
          <Modal.Header closeButton>
            <Modal.Title>Insert video</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <label className="form-label" htmlFor="kb-video-url">Video link</label>
            <input
              id="kb-video-url"
              className={`form-control${videoError ? " is-invalid" : ""}`}
              placeholder="https://www.youtube.com/watch?v=..."
              value={videoUrl}
              onChange={(e) => { setVideoUrl(e.target.value); setVideoError(""); }}
              autoFocus
            />
            {videoError ? (
              <div className="invalid-feedback d-block">{videoError}</div>
            ) : (
              <small className="text-muted">Paste a YouTube or Vimeo link.</small>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-light" onClick={closeVideoModal}>Cancel</button>
            <button type="submit" className="btn cmsd-primary">Insert</button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
}
